import * as DateFnsNs from 'date-fns'
import lodash from 'lodash'
import { type MRT_ColumnSizingState, type MRT_VisibilityState } from 'material-react-table'
import ConfirmExportDataLimitPresenter from 'src/main/ConfirmExportDataLimit.presenter'
import { excludedExportDocIds } from 'src/main/Main.scopes'
import { AppStorage, ServiceError } from 'src/utils'
import * as lang from 'src/utils/lang'
import { Logger, Presenter } from 'wdc-cube'
import FilterManager from './tcm_filter_manager'
import { TheConnectionKeys } from '../the-connection/tc_keys'
import { TheConsumerMarketPresenter } from './tcm_presenter'
import { TableEmpresaScope } from './tcm_scopes'
import {
    StringKeywordOperator,
    TheConsumerMarketService,
    type ColumnFilter,
    type ColumnSort,
    type CrmConnectionOption,
    type EmpresaFilter
} from './tcm_service'
import { type TCMTexts } from './tcm_texts'

type PaginationState = {
    pageIndex: number
    pageSize: number
}

const LOG = Logger.get('TcmPresenterForEmpresasListagem')

const service = TheConsumerMarketService.singleton()
const localStorage = AppStorage.singleton()

export default class TcmPresenterForEmpresasTableListagem extends Presenter<
    TableEmpresaScope,
    TheConsumerMarketPresenter
> {
    // :: Class methods

    constructor(owner: TheConsumerMarketPresenter) {
        super(owner, owner.scope.listagemTabs.empresasListagem)
        this.__parent = owner
        this.__texts = owner.texts
        this.__filterManager = owner.filterManager
        this.__confirmExportDataPresenter = new ConfirmExportDataLimitPresenter(
            owner.app,
            this.scope.exportDataLimitScope
        )
    }

    // :: Instance

    private readonly __parent: TheConsumerMarketPresenter
    private readonly __texts: TCMTexts
    private readonly __filterManager: FilterManager
    private readonly __confirmExportDataPresenter: ConfirmExportDataLimitPresenter

    private __pendingPushConnectionId?: number

    // :: Public API

    async initialize() {
        this.scope.update = this.update
        this.scope.caption = this.__texts.empresasListagem
        this.scope.onSelectionChange = this.action(this.__onEmpresaRowClicked)
        this.scope.onFiltrationChange = this.action(this.__onFiltrationChange)
        this.scope.onPaginationChange = this.action(this.__onPaginationChange)
        this.scope.onSortingChange = this.action(this.__onSortingChange)
        this.scope.onDownloadClick = this.action(this.__onDownloadClicked)
        this.scope.setColumnSizing = this.action(this.__onSetColumnSizing)
        this.scope.onColumnsVisibilityChange = this.action(this.__onSetColumnsVisibility)
        this.scope.onCopyCnpj = this.action(this.__onCopyCnpj)
        this.scope.onOpenCorporateRelations = this.action(this.__onOpenCorporateRelations)
        this.scope.onCrmExport = this.action(this.__onCrmExport)

        this.scope.exportDataLimitScope.onYesNoClicked = this.action(this.__onYesNoConfirmExport)
        this.scope.exportDataLimitScope.canExcludeAlreadyExported = true

        this.__confirmExportDataPresenter.initialize()

        this.initializeColumnsVisibility()

        this.loadCrmExportOptions().catch(LOG.caught)

        LOG.debug('ready')
    }

    initializeColumnsVisibility() {
        const savedVisibleColumns = localStorage.tcmCompaniesVisibleColumns
        if (savedVisibleColumns) {
            this.scope.columnVisibility = {
                ...DEFAULT_COLUMNS_VISIBILITY,
                ...(JSON.parse(savedVisibleColumns) as MRT_VisibilityState)
            }
        } else {
            this.scope.columnVisibility = DEFAULT_COLUMNS_VISIBILITY
        }
    }

    override release(): void {
        this.__confirmExportDataPresenter.release()
        super.release()
    }

    clear() {
        this.scope.pageIndex = 0
        this.scope.pageSize = 100
        this.owner.filterPane.codigo.clear()
    }

    async fetch(
        filter: EmpresaFilter,
        cfg?: {
            paginationState?: PaginationState
            sorting?: ColumnSort[]
            columnFilters?: ColumnFilter[]
        }
    ) {
        let { paginationState, sorting, columnFilters } = cfg ?? {}
        this.scope.loaded = false
        try {
            if (!columnFilters) {
                columnFilters = this.scope.filters
            }

            if (!paginationState) {
                paginationState = {
                    pageIndex: 0,
                    pageSize: this.scope.pageSize
                }
            }

            if (!sorting) {
                sorting = this.scope.sorting
            }

            let search_after: unknown
            if (paginationState.pageIndex > 0) {
                const lastRow = this.scope.dataset[this.scope.dataset.length - 1]
                if (lastRow) {
                    search_after = [lastRow.faturamento_estimado, lastRow.func_qtd, lastRow.cnpj, lastRow.score]
                }
            }

            const resp = await service.fetch({
                listagem_empresas: {
                    sorting,
                    columnFilters,
                    search_after,
                    pageSize: paginationState.pageSize,
                    withLeitos: this.__isPorteLeitosVisible(),
                    withTiposEstabelecimento: this.__isTiposEstabelecimentoVisible()
                },
                filter: filter
            })

            this.scope.pageIndex = paginationState.pageIndex
            this.scope.pageSize = paginationState.pageSize
            this.scope.sorting = sorting

            const dataset = resp.listagem_empresas ?? []
            for (let i = 0; i < dataset.length; i++) {
                const row = dataset[i]
                if (lodash.isString(row.dta_inicio_atividade)) {
                    row.dta_inicio_atividade = DateFnsNs.parseISO(row.dta_inicio_atividade)
                }
                if (lodash.isString(row.dta_situacao_cadastral)) {
                    row.dta_situacao_cadastral = DateFnsNs.parseISO(row.dta_situacao_cadastral)
                }
            }
            LOG.debug(`dataset.length=${dataset.length}`)

            if (resp.listagem_empresas_agg) {
                this.scope.rowCount = resp.listagem_empresas_agg['pj-count'].value | 0
                this.scope.total.func_qtd = resp.listagem_empresas_agg['pj-func-qtd-emp-sum'].value
                this.scope.total.faturamento_estimado = resp.listagem_empresas_agg['pj-fatu-est-emp-sum'].value
                this.scope.total.faturamento_estimado_grupo = resp.listagem_empresas_agg['pj-fatu-est-grp-sum'].value
            }
            this.scope.dataset = dataset
            this.scope.datasetVersion++

            this.__markSelected(undefined)
        } finally {
            this.scope.loaded = true
        }
    }

    private async __onEmpresaRowClicked(selectionMap: Map<string, boolean>) {
        const cnpjPresenter = this.owner.filterPane.codigo
        const inConditionScope = cnpjPresenter.getOrCreateIsIn()

        inConditionScope.value = [...selectionMap.keys()].join(', ')
        cnpjPresenter.publish()

        this.__markSelected(selectionMap)
        this.__parent.fetchData(selectionMap.size > 0 ? 'listagemDeEmpresas' : '')
    }

    private __markSelected(selectionMap: Map<string, boolean> | undefined) {
        const map = selectionMap ? selectionMap : this.collectExplicitValues()

        for (const row of this.scope.dataset) {
            row.selected = map.has(row.cnpj)
        }

        this.scope.update()
    }

    async __onFiltrationChange(columnFilters: ColumnFilter[]) {
        this.scope.filters = columnFilters
        await this.fetch(this.__filterManager.build(), {
            columnFilters
        })
    }

    async __onPaginationChange(pageIndex: number, pageSize: number) {
        if (pageSize !== this.scope.pageSize) {
            pageIndex = 0
        }
        await this.fetch(this.__filterManager.build(), {
            paginationState: { pageIndex, pageSize }
        })
    }

    async __onSortingChange(sorting: ColumnSort[]) {
        await this.fetch(this.__filterManager.build(), { sorting })
    }

    async executeDownload() {
        try {
            this.scope.working = true
            this.scope.exportDataLimitScope.isDownloading = true

            const columns = this.scope.exportColumns

            const blob = await service.downloadXlsx(
                this.__filterManager.build(),
                columns,
                'empresas',
                this.scope.exportDataLimitScope.qttExportRecords,
                this.__parent.app.compliance,
                excludedExportDocIds(this.scope.exportDataLimitScope)
            )

            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([blob]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `tcm-empresa.xlsx`)

            // Append to html link element page
            document.body.appendChild(link)
            try {
                // Start download
                link.click()
            } finally {
                // Clean up and remove the link
                link.parentNode?.removeChild(link)
            }
        } finally {
            this.scope.exportDataLimitScope.isDownloading = false
            this.scope.working = false
        }
    }

    async __onSetColumnSizing(columns: MRT_ColumnSizingState) {
        this.scope.columnSizing = columns
    }

    async __onSetColumnsVisibility(columns: MRT_VisibilityState) {
        const wasPorteLeitosVisible = this.__isPorteLeitosVisible()
        const wasTiposEstabelecimentoVisible = this.__isTiposEstabelecimentoVisible()

        this.scope.columnVisibility = columns
        localStorage.tcmCompaniesVisibleColumns = JSON.stringify(columns)

        if (
            (!wasPorteLeitosVisible && this.__isPorteLeitosVisible()) ||
            (!wasTiposEstabelecimentoVisible && this.__isTiposEstabelecimentoVisible())
        ) {
            await this.fetch(this.__filterManager.build())
        }
    }

    private __isPorteLeitosVisible() {
        return this.scope.columnVisibility?.[PORTE_LEITOS_COLUMN] !== false
    }

    private __isTiposEstabelecimentoVisible() {
        return this.scope.columnVisibility?.[TIPO_ESTABELECIMENTO_COLUMN] !== false
    }

    async __onDownloadClicked(columns: string[]) {
        this.scope.exportColumns = columns

        this.scope.exportDataLimitScope.isChecking = true
        this.scope.update()
        try {
            const limitExportData = await service.checkLimitExportData(this.__filterManager.build(), 'empresas')
            await this.scope.exportDataLimitScope.onCheckLimitExportData(limitExportData)
        } finally {
            this.scope.exportDataLimitScope.isChecking = false
        }
    }

    protected async __onYesNoConfirmExport(yes: boolean) {
        LOG.debug(`__onYesNoConfirmExport: yes=${yes}`)
        if (yes) {
            if (this.__pendingPushConnectionId != null) {
                await this.executePush(this.__pendingPushConnectionId, true)
            } else {
                await this.executeDownload()
            }
        }
        this.__pendingPushConnectionId = undefined
        this.scope.exportDataLimitScope.open = false
        this.scope.update()
    }

    async __onCopyCnpj() {
        if (!navigator.clipboard) {
            this.__parent.app.alert('error', 'Aviso', 'Área de transferência não disponível')
            return
        }

        const cnpjArray = this.collectSelectedCnpjs()
        await navigator.clipboard.writeText(cnpjArray.join(','))
    }

    async __onOpenCorporateRelations() {
        const cnpjArray = this.collectSelectedCnpjs()
        if (cnpjArray.length === 0) {
            return
        }

        const target = new TheConnectionKeys(this.__parent.app)
        target.companyIds = cnpjArray
        await target.flip()
    }

    async __onCrmExport(connectionId: number) {
        await this.executePush(connectionId, false)
    }

    private async executePush(connectionId: number, confirmOverLimit: boolean) {
        const cnpjs = this.collectSelectedCnpjs()
        if (cnpjs.length === 0) {
            return
        }

        try {
            this.scope.working = true
            const result = await service.pushToCrm({ cnpjs, connectionId, confirmOverLimit })

            if (result.kind === 'franchise_exceeded') {
                this.__pendingPushConnectionId = connectionId
                await this.scope.exportDataLimitScope.onCheckLimitExportData(result.franchise)
                return
            }

            const msg = result.deduped
                ? 'Esta seleção já estava na fila de exportação.'
                : 'Exportação enviada para a fila com sucesso.'
            this.__parent.app.alert('success', 'Exportação para CRM', msg)
        } catch (caught) {
            this.__parent.app.alert('error', 'Exportação para CRM', lang.toError(caught).message)
        } finally {
            this.scope.working = false
            this.scope.update()
        }
    }

    private collectSelectedCnpjs(): string[] {
        const cnpjArray: string[] = []
        for (const row of this.scope.dataset) {
            if (row.selected) {
                cnpjArray.push(row.cnpj)
            }
        }
        return cnpjArray
    }

    private async loadCrmExportOptions() {
        const groupId = await service.fetchPreferredGroupId()
        if (groupId == null) {
            this.scope.crmExportOptions = []
            return
        }

        let connections: CrmConnectionOption[]
        try {
            connections = await service.fetchCrmConnections(groupId)
        } catch (exn) {
            if (exn instanceof ServiceError && exn.statusCode === 403) {
                LOG.debug('Enriquecimento CRM externo nao habilitado para o grupo', groupId)
                this.scope.crmExportOptions = []
                this.scope.update()
                return
            }
            throw exn
        }

        this.scope.crmExportOptions = connections.map((c) => ({
            connectionId: c.id,
            crmType: c.crm_type,
            name: c.name
        }))
        this.scope.update()
    }

    private collectExplicitValues() {
        const prop = this.__filterManager.empresaCodigo

        const codigoValMap = new Map<string, boolean>()
        for (const filterVal of prop.values()) {
            if (filterVal.value) {
                if (filterVal.operator == StringKeywordOperator.is_in) {
                    lang.splitValues(filterVal.value).forEach((codigo) => codigo && codigoValMap.set(codigo, true))
                    continue
                }

                if (filterVal.operator == StringKeywordOperator.is_not_in) {
                    lang.splitValues(filterVal.value).forEach((codigo) => codigoValMap.delete(codigo))
                    continue
                }

                if (filterVal.operator == StringKeywordOperator.is) {
                    const codigo = lodash.trim(filterVal.value.trim())
                    codigo && codigoValMap.set(codigo, true)
                    continue
                }

                if (filterVal.operator == StringKeywordOperator.is_not) {
                    codigoValMap.delete(filterVal.value)
                    continue
                }
            }
        }
        return codigoValMap
    }

    override onBeforeScopeUpdate() {
        let checkedRowCount = 0
        for (const row of this.scope.dataset) {
            if (row.selected) {
                checkedRowCount++
            }
        }
        this.scope.checkedRowCount = checkedRowCount
    }
}

const DEFAULT_COLUMNS_VISIBILITY: MRT_VisibilityState = {
    cnpj: true,
    cnes: false,
    nome: true,
    tipo_estabelecimento_saude: true,
    natureza_juridica: false,
    natureza_juridica_grp: false,
    situacao_cadastral_nme: false,
    dta_situacao_cadastral: false,
    dta_inicio_atividade: false,
    cnae: true,
    cnae_descricao: true,
    endereco_cep: false,
    endereco_rua_nome: false,
    endereco_numero: false,
    endereco_complemento: false,
    endereco_bairro_nome: false,
    endereco_cidade_nome: true,
    endereco_uf: true,
    regiao_metropolitana: false,
    regiao_geo_imediata: false,
    regiao_geo_intermediaria: false,
    endereco_lat: false,
    endereco_lon: false,
    socios_nomes: false,
    emp_captial_social: false,
    emp_idade_anos: false,
    emp_qtd_grupo: false,
    func_qtd: true,
    func_qtd_masc: false,
    func_qtd_fem: false,
    func_qtd_0_a_18: false,
    func_qtd_19_a_33: false,
    func_qtd_34_a_48: false,
    func_qtd_49_a_58: false,
    func_qtd_59_ou_mais: false,
    faturamento_estimado: true,
    faturamento_estimado_grupo: true,
    matriz_cnpj: false,
    matriz_nome: false,
    matriz_uf: false,
    grupo_cnpj: false,
    telefone1: false,
    telefone2: false,
    email: false,
    salario_total: false,
    salario_total_grupo: false,
    func_qtd_matriz_e_filiais: false,
    qtd_filiais: false,
    web_nome: false,
    web_categoria: false,
    web_nota: false,
    web_qtd_avaliacoes: false,
    web_site: false,
    web_tel: false,
    web_tempo_medio: false,
    optante_simples: false,
    porte_codigo: false,
    porte_funcionarios: false,
    porte_fat_cnpj: false,
    porte_fat_grupo: false,
    porte_leitos: false,
    regime_tributario: false,
    score_risco: false,
    cadeia_valor_saude: false
}

const PORTE_LEITOS_COLUMN = 'porte_leitos'
const TIPO_ESTABELECIMENTO_COLUMN = 'tipo_estabelecimento_saude'
